/** Rótulos e tons dos estados do domínio (tickets e shipments). */
import i18n from '../core/i18n/i18n'
import type { Status } from '../core/api/types'
import type { Lang } from '../core/i18n/messages'

export type Tone = 'neutral' | 'info' | 'warning' | 'success' | 'error' | 'primary' | 'purple' | 'pink'

interface StatusMeta {
  label: string
  tone: Tone
}

function meta(key: string, fallback: string, tone: Tone): StatusMeta {
  return {
    get label() {
      return String(i18n.t(key, { defaultValue: fallback }))
    },
    tone,
  }
}

/** Status do ticket (pedido de embarque). */
export const ticketStatus: Record<string, StatusMeta> = {
  draft: meta('status.ticket.draft', 'Rascunho', 'neutral'),
  submitted: meta('status.ticket.submitted', 'Enviada', 'info'),
  under_review: meta('status.ticket.underReview', 'Em análise', 'warning'),
  approved: meta('status.ticket.approved', 'Aprovada', 'success'),
  in_progress: meta('status.ticket.inProgress', 'Em execução', 'primary'),
  completed: meta('status.ticket.completed', 'Concluída', 'success'),
  cancelled: meta('status.ticket.cancelled', 'Cancelada', 'error'),
}

/** Andamento do shipment (fazenda→UBS).
 *  `draft` = carga criada no envio da solicitação (antes da aprovação). */
export const shipmentStatus: Record<string, StatusMeta> = {
  draft: meta('status.shipment.draft', 'Rascunho', 'neutral'),
  awaiting_harvest: meta('status.shipment.awaitingHarvest', 'Aguardando colheita', 'neutral'),
  loading: meta('status.shipment.loading', 'Carregando', 'info'),
  awaiting_invoice: meta('status.shipment.awaitingInvoice', 'Aguardando nota', 'warning'),
  awaiting_unload: meta('status.shipment.awaitingUnload', 'Aguardando descarga', 'warning'),
  unloading: meta('status.shipment.unloading', 'Descarregando', 'info'),
  released: meta('status.shipment.released', 'Liberado', 'success'),
  cancelled: meta('status.shipment.cancelled', 'Cancelada', 'error'),
}

/** Estados de cadastro genéricos (active/inactive/blocked/maintenance). */
export const cadastroStatus: Record<string, StatusMeta> = {
  active: meta('status.record.active', 'Ativo', 'success'),
  inactive: meta('status.record.inactive', 'Inativo', 'neutral'),
  blocked: meta('status.record.blocked', 'Bloqueado', 'error'),
  maintenance: meta('status.record.maintenance', 'Manutenção', 'warning'),
  // Pessoas preserva o vocabulário de RH sem alargar o status técnico comum a
  // todos os cadastros. As chaves em português vêm prontas da API.
  ativo: meta('status.personnel.active', 'Ativo', 'success'),
  afastado: meta('status.personnel.away', 'Afastado', 'warning'),
  ferias: meta('status.personnel.vacation', 'Férias', 'info'),
  demitido: meta('status.personnel.dismissed', 'Demitido', 'neutral'),
  bloqueado: meta('status.personnel.blocked', 'Bloqueado', 'error'),
}

/** Cockpit de contratos: filtra/rotula pelo STATUS do próprio contrato (2 buckets). */
export const contractStatusMeta: Record<string, StatusMeta> = {
  active: meta('status.contract.active', 'Em andamento', 'primary'),
  closed: meta('status.contract.closed', 'Finalizado', 'success'),
}

/** Situação do contrato, marcada à mão no cabeçalho da Frente de Trabalho: manual
 *  desde 24/09/2026, sem marca é Previsto. É ela que rotula, filtra e conta a grade
 *  de Contratos. */
export const contractSituacao: Record<string, StatusMeta> = {
  previsto: meta('status.contractSituation.planned', 'Previsto', 'info'),
  // Campo com máquina já posicionada e serviço ainda não iniciado (Samuel,
  // 24/09/2026): "temos que ter o controle para saber os campos que já posicionamos
  // máquinas". Rosa, a cor que ele escolheu, e que nenhuma outra situação usa.
  aguardando_liberacao: meta('status.contractSituation.awaitingRelease', 'Aguardando liberação', 'pink'),
  em_execucao: meta('status.contractSituation.inProgress', 'Em execução', 'primary'),
  // Contrato suspenso por decisão da operação (Samuel, 18/09/2026): "não finalizou
  // mas também não iniciou, e vai ter que paralisar o contrato alguns dias". Roxo:
  // não é erro, não é espera e não é conclusão.
  paralisado: meta('status.contractSituation.halted', 'Paralisado', 'purple'),
  finalizado: meta('status.contractSituation.completed', 'Finalizado', 'success'),
  finalizado_com_pendencias: meta('status.contractSituation.completedWithIssues', 'Finalizado c/ pendências', 'warning'),
}

/** Status nota (catálogo `invoice_status`) — cor suave por code na grade de Cargas. */
export const invoiceStatusMeta: Record<string, StatusMeta> = {
  pendente: meta('status.invoice.pending', 'Pendente', 'warning'),
  solicitada: meta('status.invoice.requested', 'Solicitada', 'info'),
  cancelada: meta('status.invoice.cancelled', 'Cancelada', 'error'),
}

/** Status viagem (catálogo `trip_status`) — MÁQUINA operacional única do veículo
 *  (Complemento §1/§5): Escalado→Carregando→Trânsito→Descarregando→Atendido, com
 *  "Não atendido" encerrando antes. "Observação" saiu daqui e virou ALERTA separado
 *  (has_observation). `escalado` entra sozinho na atribuição do motorista. */
export const tripStatusMeta: Record<string, StatusMeta> = {
  escalado: meta('status.trip.scheduled', 'Escalado', 'warning'),
  carregando: meta('status.trip.loading', 'Carregando', 'warning'),
  transito: meta('status.trip.inTransit', 'Trânsito', 'primary'),
  descarregando: meta('status.trip.unloading', 'Descarregando', 'info'),
  atendido: meta('status.trip.attended', 'Atendido', 'success'),
  nao_atendido: meta('status.trip.unattended', 'Não Atendido', 'error'),
}

/** Ordem da máquina de trip_status (etapas operacionais). Estados finais separados. */
export const TRIP_STATUS_ORDER = ['escalado', 'carregando', 'transito', 'descarregando'] as const
export const TRIP_STATUS_TERMINAIS = ['atendido', 'nao_atendido'] as const

/** Solicitação VP (Protheus) — tag compacta na grade de Cargas (Mudanças v1 §6). */
export const vpRequestMeta: Record<string, StatusMeta> = {
  solicitado: meta('status.vp.requested', 'Solicitado', 'success'),
  nao_solicitado: meta('status.vp.notRequested', 'Não solicitado', 'neutral'),
  aguardando_sync: meta('status.vp.awaitingSync', 'Aguardando sincronização', 'warning'),
  erro_sync: meta('status.vp.syncError', 'Erro de sincronização', 'error'),
}

/** Status da VIAGEM de prancha (catálogo `transport_status`). `andamento` e
 *  `finalizado` são automáticos; `apontado` é marcação manual do operador. */
export const transportStatusMeta: Record<string, StatusMeta> = {
  andamento: meta('status.transport.inProgress', 'Andamento', 'primary'),
  finalizado: meta('status.transport.completed', 'Finalizado', 'success'),
  apontado: meta('status.transport.reported', 'Apontado', 'info'),
  // Códigos legados, mantidos só para não quebrar registro antigo na tela.
  scheduled: meta('status.transport.scheduledLegacy', 'Apontada', 'info'),
  in_transit: meta('status.transport.inTransitLegacy', 'Trânsito', 'warning'),
  finished: meta('status.transport.finishedLegacy', 'Finalizado', 'success'),
}

/** Status do VEÍCULO na viagem de prancha (`transport_vehicle_status`), avançado
 *  pela geocerca: chega na origem, sai dela e chega no destino. */
export const transportVehicleStatusMeta: Record<string, StatusMeta> = {
  escalado: meta('status.transportVehicle.scheduled', 'Escalado', 'warning'),
  embarcando: meta('status.transportVehicle.boarding', 'Embarcando', 'warning'),
  transito: meta('status.transportVehicle.inTransit', 'Trânsito', 'primary'),
  disponivel: meta('status.transportVehicle.available', 'Disponível', 'success'),
}

/** Ordem natural do status do VEÍCULO na prancha. */
export const TRANSPORT_VEHICLE_ORDER = ['escalado', 'embarcando', 'transito', 'disponivel'] as const

/** Transições que a operação faz sem explicar nada — espelho de
 *  `transporte_geofence_service.VEHICLE_TRANSICOES_LIVRES`. `escalado → transito`
 *  está aqui de propósito: é a prancha que segue para outro ponto sem passar pela
 *  cerca de embarque. Qualquer outra mudança é CORREÇÃO e pede justificativa; quem
 *  decide de verdade é o backend, isto só evita pedir motivo à toa. */
const TRANSPORT_VEHICLE_LIVRES: Record<string, readonly string[]> = {
  '': ['escalado'],
  escalado: ['embarcando', 'transito'],
  embarcando: ['transito'],
  transito: ['disponivel'],
  disponivel: [],
}

export function transporteVehicleTransicaoLivre(atual: string | null | undefined, novo: string): boolean {
  return TRANSPORT_VEHICLE_LIVRES[atual ?? '']?.includes(novo) ?? false
}

/** O STATUS ÚNICO da movimentação de prancha (Samuel, 15/10/2026): "Remover a
 *  separação entre Status do Veículo e Status da Viagem. Cada registro terá apenas um
 *  status." O servidor o deriva dos dois eixos que o banco guarda, em
 *  `transporte_geofence_service.situacao_da_viagem`, e é ele que a grade lê e escreve. */
export const transporteSituacaoMeta: Record<string, StatusMeta> = {
  // A viagem prevista (Samuel, 28/10/2026): planejada, esperando a predecessora.
  previsto: meta('status.transporteSituacao.previsto', 'Previsto', 'neutral'),
  escalado: meta('status.transporteSituacao.escalado', 'Escalado', 'warning'),
  embarcando: meta('status.transporteSituacao.embarcando', 'Embarcando', 'warning'),
  transito: meta('status.transporteSituacao.transito', 'Trânsito', 'primary'),
  finalizado: meta('status.transporteSituacao.finalizado', 'Finalizado', 'error'),
  apontado: meta('status.transporteSituacao.apontado', 'Apontada', 'success'),
}

/** A ordem da operação, a mesma da tabela do documento. É o que a grade escolhe à mão:
 *  Previsto fica de fora, porque nasce da escolha da predecessora (28/10/2026). */
export const TRANSPORTE_SITUACOES = ['escalado', 'embarcando', 'transito', 'finalizado', 'apontado'] as const

/** Espelho de `transporte_geofence_service.SITUACAO_TRANSICOES_LIVRES`. O resto é
 *  correção e pede justificativa; quem decide de verdade é o backend. */
const TRANSPORTE_SITUACAO_LIVRES: Record<string, readonly string[]> = {
  '': ['escalado'],
  // A prevista não muda à mão: vira Escalado sozinha quando a predecessora finaliza.
  previsto: [],
  escalado: ['embarcando', 'transito'],
  embarcando: ['transito'],
  transito: ['finalizado'],
  finalizado: ['apontado'],
  apontado: [],
}

export function transporteSituacaoTransicaoLivre(atual: string | null | undefined, nova: string): boolean {
  return TRANSPORTE_SITUACAO_LIVRES[atual ?? '']?.includes(nova) ?? false
}

const TRANSPORTE_ANTES_DA_SAIDA = ['', 'previsto', 'escalado', 'embarcando']
const TRANSPORTE_ENCERRADAS = ['finalizado', 'apontado']

/** Que datas a troca MANUAL de status pede (Samuel, 22/10/2026): de Escalado para
 *  Trânsito só o Início, de Trânsito para Finalizado só o Fim, de Escalado para
 *  Finalizado os dois. Espelho de `transporte_geofence_service.datas_pedidas`; quem
 *  recusa de verdade é o backend. A máquina que foi rodando não tem status. */
export function transporteDatasPedidas(
  atual: string | null | undefined, nova: string | null | undefined,
  inicioAtual: string | null | undefined, semPrancha?: boolean,
): { inicio: boolean; fim: boolean } {
  const de = atual ?? ''
  const para = nova ?? ''
  if (semPrancha || de === para) return { inicio: false, fim: false }
  const semInicio = TRANSPORTE_ANTES_DA_SAIDA.includes(de) || !inicioAtual
  if (para === 'transito') return { inicio: semInicio, fim: false }
  if (TRANSPORTE_ENCERRADAS.includes(para) && !TRANSPORTE_ENCERRADAS.includes(de)) return { inicio: semInicio, fim: true }
  return { inicio: false, fim: false }
}

/** Que datas da prancha a correção à mão alcança (Samuel, 30/10/2026): o Início
 *  depois da saída e o Fim na viagem encerrada. A correção muda o valor, nunca cria a
 *  data que a etapa ainda não tem. Espelho de `editar_datas_da_prancha` no backend. */
export function transporteDatasCorrigiveis(
  situacao: string | null | undefined, semPrancha?: boolean,
): { inicio: boolean; fim: boolean } {
  if (semPrancha) return { inicio: false, fim: false }
  const s = situacao ?? ''
  return { inicio: !TRANSPORTE_ANTES_DA_SAIDA.includes(s), fim: TRANSPORTE_ENCERRADAS.includes(s) }
}

/** Status derivado da frota (lista de descarga única). Códigos ingleses. */
export const frotaStatus: Record<string, StatusMeta> = {
  unloading: meta('status.fleet.unloading', 'Descarregando', 'info'),
  transit: meta('status.fleet.inTransit', 'Trânsito', 'primary'),
  loading: meta('status.fleet.loading', 'Carregando', 'warning'),
  available: meta('status.fleet.available', 'Disponível', 'success'),
  on_leave: meta('status.fleet.onLeave', 'Folga', 'neutral'),
}

/** Prioridade do ticket. Códigos ingleses, rótulo pt-BR. */
export const prioridadeMeta: Record<string, StatusMeta> = {
  low: meta('status.priority.low', 'Baixa', 'neutral'),
  normal: meta('status.priority.normal', 'Normal', 'info'),
  high: meta('status.priority.high', 'Alta', 'warning'),
  urgent: meta('status.priority.urgent', 'Urgente', 'error'),
}

/** Motivos de desvio operacionais (catálogo `deviation`). */
export const deviationMeta: Record<string, StatusMeta> = {
  attended: meta('status.deviation.attended', 'Atendido', 'success'),
  rain: meta('status.deviation.rain', 'Chuva no campo', 'warning'),
  humidity: meta('status.deviation.humidity', 'Umidade', 'warning'),
  machine_broken: meta('status.deviation.machineBroken', 'Máquina quebrada', 'error'),
  vehicle_broken: meta('status.deviation.vehicleBroken', 'Veículo quebrado', 'error'),
  no_vehicle: meta('status.deviation.noVehicle', 'Falta de veículo', 'error'),
  low_yield: meta('status.deviation.lowYield', 'Baixo rendimento', 'warning'),
  other: meta('status.deviation.other', 'Outros', 'neutral'),
}

/** Cor do "farolzinho" do contrato na Frente de Trabalho, na planilha SAFRA INVERNO.
 *  Verde finalizado, vermelho finalizado com pendência, âmbar em andamento, azul
 *  previsto, rosa aguardando liberação e roxo paralisado. NÃO reutiliza
 *  `contractSituacao`, onde amarelo e vermelho não coincidem: é mapa próprio da
 *  Frente. */
export const FRENTE_CONTRACT_DOT: Record<string, { color: string; label: string }> = {
  finalizado: { color: '#16a34a', get label() { return meta('status.contractSituation.completed', 'Finalizado', 'success').label } }, // verde
  finalizado_com_pendencias: { color: '#dc2626', get label() { return meta('status.contractSituation.completedWithIssue', 'Finalizado c/ pendência', 'warning').label } }, // vermelho
  em_execucao: { color: '#d97706', get label() { return meta('status.contract.active', 'Em andamento', 'primary').label } }, // âmbar
  previsto: { color: '#0891b2', get label() { return meta('status.contractSituation.planned', 'Previsto', 'info').label } }, // azul
  // Rosa do Aguardando liberação (Samuel, 24/09/2026), o mesmo do tom `pink`.
  aguardando_liberacao: { color: '#db2777', get label() { return meta('status.contractSituation.awaitingRelease', 'Aguardando liberação', 'pink').label } }, // rosa
  // Roxo do Paralisado (Samuel, 18/09/2026), o mesmo violeta da linha de Outra
  // Transportadora na grade de cargas: as duas dizem que existe e não está rodando.
  paralisado: { color: '#7c3aed', get label() { return meta('status.contractSituation.halted', 'Paralisado', 'purple').label } }, // roxo
}

/** Status do Painel de Descarga, na ORDEM da operação: é a ordem da coluna, do
 *  filtro e dos indicadores, porque todos leem `Object.keys` daqui. Desde 22/09/2026
 *  é a do §8 do pedido do Samuel, com o Adiantando na frente. Espelha
 *  `DESCARGA_STATUS_ORDER` do backend. */
export const descargaStatus: Record<string, StatusMeta> = {
  advancing: meta('status.unloadingPanel.advancing', 'Adiantando', 'warning'),
  available: meta('status.unloadingPanel.available', 'Disponível', 'success'),
  unloading: meta('status.unloadingPanel.unloading', 'Descarregando', 'info'),
  transit: meta('status.unloadingPanel.inTransit', 'Em trânsito', 'primary'),
  loading: meta('status.unloadingPanel.loading', 'Carregando', 'warning'),
  scheduled: meta('status.unloadingPanel.scheduled', 'Escalado', 'warning'),
  maintenance: meta('status.unloadingPanel.maintenance', 'Manutenção', 'error'),
  day_off: meta('status.unloadingPanel.dayOff', 'Folga', 'neutral'),
  // O antigo Outro Prog., que o HUB não usava, desde 01/10/2026 é o conjunto escalado
  // numa viagem do módulo Outros Serviços Transporte.
  other_prog: meta('status.unloadingPanel.otherSchedule', 'Outros Serviços', 'purple'),
  operators: meta('status.unloadingPanel.operators', 'Operadores', 'neutral'),
  away: meta('status.unloadingPanel.away', 'Afastado', 'neutral'),
}

/** Estados do conjunto que correspondem a uma ETAPA DA VIAGEM em execução: são os
 *  únicos com relógio de ciclo (tempo no status e data do escalonamento). Espelha
 *  `_TRIP_TO_DESCARGA` do backend, na mesma ordem da operação. */
export const DESCARGA_ETAPAS_VIAGEM = ['scheduled', 'loading', 'transit', 'unloading'] as const

/** Estados que o Painel de Descarga NÃO oferece para escolher à mão, porque quem os
 *  escreve é a escala e a geocerca (Samuel, 25/08/2026: "são apontamentos automáticos
 *  e se precisar mudar, muda lá pela frente de trabalho").
 *
 *  São as QUATRO etapas de viagem. Descarregando entrou na correção do mesmo dia,
 *  "perdão, eu esqueci de falar descarregando que é automático também" — a primeira
 *  lista tinha só três, e ela deixava passar a única etapa que a geocerca fecha no
 *  destino. Derivado de `DESCARGA_ETAPAS_VIAGEM` para as duas não divergirem: o que
 *  a viagem aponta sozinha é exatamente o que a grade não deixa digitar.
 *
 *  Continuam FILTRÁVEIS. Aparecem na coluna, e procurar por elas é leitura, não
 *  marcação. */
export const DESCARGA_ETAPAS_VIAGEM_AUTOMATICAS: readonly string[] = DESCARGA_ETAPAS_VIAGEM

/** Estados que o HUB não usa e saíram também do FILTRO (Samuel, 25/08/2026: "pois
 *  não vai ter no HUB"). O rótulo continua no dicionário acima para que uma linha
 *  antiga com esse status ainda apareça escrita por extenso, e não como código. */
export const DESCARGA_STATUS_FORA_DO_HUB: string[] = ['operators']

/** Estados que só a escala do módulo Outros Serviços Transporte grava (01/10/2026).
 *  Aparecem na coluna e no filtro, e ninguém os escolhe à mão no Painel. */
export const DESCARGA_STATUS_SO_DO_MODULO: string[] = ['other_prog']

/** Estados OPERACIONAIS do conjunto, que formam o histórico do Painel de Descarga.
 *  Espelha `DESCARGA_STATUS_CONJUNTO` do backend. */
export const DESCARGA_ESTADOS_CONJUNTO = [
  'advancing', 'available', 'scheduled', 'maintenance', 'day_off', 'other_prog', 'operators', 'away',
] as const

/** Fallback amigável para status desconhecido. */
export function humanize(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')
}

// ── Legendas do Painel Cockpit do Protheus (prints do Samuel, 01/09/2026) ────
// As bolinhas da grade de cargas usam EXATAMENTE estas cores, uma por legenda.
// A chave é o texto da origem normalizado (maiúsculo, sem acento): o vocabulário
// do ERP já trocou inteiro num dia (28/08/2026), então status desconhecido não
// pode quebrar — cai numa bolinha neutra com o texto cru no tooltip.

export interface ProtheusDot { color: string; label: string; x?: boolean }

const foldStatus = (raw: string): string =>
  raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase()

/** Legenda Viagem — coluna STATUS DA VIAGEM. */
export const protheusViagemDot: Record<string, ProtheusDot> = {
  'EM ABERTO': { color: '#16a34a', label: 'Em Aberto' },            // verde
  'FECHADA': { color: '#dc2626', label: 'Fechada' },                // vermelho
  'EM TRANSITO': { color: '#eab308', label: 'Em Trânsito' },        // amarelo
  'CHEGADA EM FILIAL': { color: '#ea580c', label: 'Chegada em Filial' }, // laranja
  'ENCERRADA': { color: '#2563eb', label: 'Encerrada' },            // azul
  'CANCELADA': { color: '#6b7280', label: 'Cancelada' },            // cinza
}

/** Legenda Informações Vale Pedágio — coluna STATUS VP. O dado por viagem chegou
 *  em 08/09/2026, em `ocorrencia.status_pedagio`, e acendeu este mapa, que estava
 *  escrito e apagado desde 01/09. */
export const protheusVpDot: Record<string, ProtheusDot> = {
  'NAO POSSUI PEDAGIO': { color: '#6b7280', label: 'Não Possui Pedágio' },      // cinza
  'PENDENTE DE INFORMACOES': { color: '#dc2626', label: 'Pendente de Informações' }, // vermelho
  'PREENCHIDO': { color: '#16a34a', label: 'Preenchido' },                      // verde
}

/** Legenda Solicitação Documentação — coluna STATUS NF, em `notas[].status_solicitacao`.
 *  `C` é um X vermelho, não uma bolinha.
 *
 *  OS DOIS VOCABULÁRIOS ESTÃO AQUI DE PROPÓSITO. O mapa nasceu pelos CÓDIGOS da
 *  legenda do Cockpit, e a carga real manda a PALAVRA: "Confirmado". Com só os
 *  códigos, toda linha caía na bolinha neutra cinza — que não some, mas também não
 *  diz nada. Cobrir os dois é a mesma decisão de `PlanilhasPage.toneDoStatus`: esta
 *  origem já trocou um vocabulário inteiro de um dia para o outro, e o mapa que
 *  aceita os dois atravessa a troca sem deploy. */
export const protheusNfDot: Record<string, ProtheusDot> = {
  '1': { color: '#16a34a', label: 'Em Aberto' },    // verde
  '2': { color: '#eab308', label: 'Solicitado' },   // amarelo
  '3': { color: '#6b7280', label: 'Em Execução' },  // cinza
  '9': { color: '#2563eb', label: 'Confirmado' },   // azul
  C: { color: '#dc2626', label: 'Cancelado', x: true },
  'EM ABERTO': { color: '#16a34a', label: 'Em Aberto' },
  SOLICITADO: { color: '#eab308', label: 'Solicitado' },
  'EM EXECUCAO': { color: '#6b7280', label: 'Em Execução' },
  CONFIRMADO: { color: '#2563eb', label: 'Confirmado' },
  CANCELADO: { color: '#dc2626', label: 'Cancelado', x: true },
}

/** Resolve o status cru da origem na legenda; desconhecido vira bolinha neutra
 *  com o texto cru, para o vocabulário novo aparecer em vez de sumir. */
export function protheusDotDe(mapa: Record<string, ProtheusDot>, raw: string | null | undefined): ProtheusDot | null {
  const texto = (raw ?? '').trim()
  if (!texto) return null
  return mapa[foldStatus(texto)] ?? { color: '#94a3b8', label: texto }
}

/** Resolve um registro do catálogo universal no idioma atual.
 *  Para domínios conhecidos, usa os dicionários PT/EN/ES do frontend. Para um
 *  contexto extensível vindo do backend, usa `label_en` em inglês e `label_pt`
 *  como fallback nos demais idiomas (a API ainda não possui `label_es`). */
export function catalogStatusLabel(status: Status, lang: Lang): string {
  const catalogs: Record<string, Record<string, StatusMeta>> = {
    ticket: ticketStatus,
    shipment: shipmentStatus,
    cadastro: cadastroStatus,
    contract: contractStatusMeta,
    invoice_status: invoiceStatusMeta,
    trip_status: tripStatusMeta,
    transport_status: transportStatusMeta,
    transport_vehicle_status: transportVehicleStatusMeta,
    deviation: deviationMeta,
  }
  const localized = catalogs[status.context]?.[status.code]?.label
  if (localized) return localized
  return lang === 'en' ? (status.label_en || status.label_pt) : status.label_pt
}
